import { useEffect, useState } from 'react';
import { useVelvet } from '@/context/VelvetContext';
import { useAuth } from '@/context/AuthContext';
import { apiFetch, supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Phone, MapPin, Camera, X, ChevronRight, Package, ClipboardList, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const STATUS_LABEL = {
  Pending: 'Pending', Assigned: 'Assigned', 'En Route': 'En route', Arrived: 'Arrived',
  Delivered: 'Delivered', Failed: 'Failed',
};

export default function MyDeliveries() {
  const { deliveries, orders, customers, reloadDeliveries, reloadOrders } = useVelvet();
  const { profile, role } = useAuth();
  const [detailId, setDetailId] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [failOpen, setFailOpen] = useState(false);

  // Filter to my deliveries (drivers see own via RLS; admins/managers see all)
  const mine = role === 'driver'
    ? deliveries.filter((d) => d.driver_id === profile?.id)
    : deliveries;

  const active = mine.filter((d) => !['Delivered','Failed'].includes(d.status));
  const past = mine.filter((d) => ['Delivered','Failed'].includes(d.status));

  const detail = detailId ? mine.find((d) => d.id === detailId) : null;
  const order = detail ? orders.find((o) => o.id === detail.order_id) : null;
  const cust = order ? customers.find((c) => c.id === order.customer_id) : null;

  const action = async (act, notes) => {
    try {
      await apiFetch(`/deliveries/${detail.id}/action`, {
        method: 'POST',
        body: JSON.stringify({ action: act, notes }),
      });
      toast.success('Updated');
      reloadDeliveries();
      reloadOrders();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-2xl mx-auto" data-testid="my-deliveries-page">
      <div className="mb-6">
        <div className="velvet-eyebrow mb-2">Today</div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight">My deliveries</h1>
        <p className="text-sm text-muted-foreground mt-2">Only your assigned deliveries appear here.</p>
      </div>

      <section className="mb-8">
        <div className="velvet-eyebrow mb-3">Active <span className="text-muted-foreground">· {active.length}</span></div>
        {active.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center">No active deliveries right now.</p>
        ) : (
          <ul className="space-y-3">
            {active.map((d) => {
              const o = orders.find((x) => x.id === d.order_id);
              const c = customers.find((x) => x.id === o?.customer_id);
              return (
                <li key={d.id}>
                  <button onClick={() => setDetailId(d.id)} className="w-full text-left velvet-card p-4 hover:-translate-y-[2px] transition-transform" data-testid={`delivery-card-${d.id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-mono text-[11px] text-muted-foreground">{o?.number}</div>
                      <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border border-border">{STATUS_LABEL[d.status]}</span>
                    </div>
                    <div className="font-serif text-xl mt-1">{d.recipient_name || c?.name || 'Recipient'}</div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{o?.address}</div>
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{o?.delivery_date ? new Date(o.delivery_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' }) : '—'}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {past.length > 0 && (
        <section>
          <div className="velvet-eyebrow mb-3">Completed today</div>
          <ul className="space-y-2">
            {past.slice(0, 10).map((d) => {
              const o = orders.find((x) => x.id === d.order_id);
              return (
                <li key={d.id} className="flex items-center justify-between px-4 py-2 border border-border bg-background/60 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">{o?.number}</span>
                  <span>{d.recipient_name || '—'}</span>
                  <span className={`text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 border ${d.status === 'Delivered' ? 'bg-foreground text-background border-foreground' : 'text-destructive border-destructive/40'}`}>
                    {STATUS_LABEL[d.status]}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Detail sheet */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-foreground/50 flex items-end sm:items-center sm:justify-center" onClick={() => setDetailId(null)}>
          <div className="w-full sm:max-w-lg bg-card border-t sm:border border-border max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="delivery-detail">
            <div className="p-5 flex items-start justify-between border-b border-border sticky top-0 bg-card">
              <div>
                <div className="velvet-eyebrow">{order?.number}</div>
                <div className="font-serif text-2xl tracking-tight">{detail.recipient_name || cust?.name || 'Recipient'}</div>
              </div>
              <button onClick={() => setDetailId(null)} className="p-1"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              <InfoBlock label="Address" icon={MapPin}>
                <div className="text-sm">{order?.address || '—'}</div>
                {order?.address && (
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(order.address)}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs underline" data-testid="open-maps-btn">
                    Open in Maps
                  </a>
                )}
              </InfoBlock>

              <InfoBlock label="Recipient phone" icon={Phone}>
                {cust?.phone ? (
                  <a href={`tel:${cust.phone}`} className="inline-flex items-center gap-2 text-sm border border-border px-3 py-2 bg-background hover:bg-[hsl(var(--muted))]" data-testid="call-btn">
                    <Phone className="h-3.5 w-3.5" /> {cust.phone}
                  </a>
                ) : <span className="text-sm text-muted-foreground">Not provided</span>}
              </InfoBlock>

              <InfoBlock label="Window" icon={Timer}>
                <div className="text-sm">{order?.delivery_date ? new Date(order.delivery_date).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : '—'}</div>
              </InfoBlock>

              {detail.delivery_instructions && (
                <InfoBlock label="Delivery instructions" icon={ClipboardList}>
                  <div className="text-sm whitespace-pre-wrap">{detail.delivery_instructions}</div>
                </InfoBlock>
              )}

              <InfoBlock label="Arrangement" icon={Package}>
                <ul className="text-sm space-y-1">
                  {(order?.order_lines || []).map((li) => (
                    <li key={li.id} className="flex justify-between">
                      <span>{li.item_name}</span>
                      <span className="font-mono text-muted-foreground">×{li.qty}</span>
                    </li>
                  ))}
                </ul>
              </InfoBlock>

              {/* Action buttons */}
              <div className="pt-2 space-y-2">
                {detail.status === 'Assigned' && (
                  <Button onClick={() => action('start_route')} className="w-full rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="start-route-btn">
                    Start route
                  </Button>
                )}
                {detail.status === 'En Route' && (
                  <>
                    <Button onClick={() => action('arrived')} className="w-full rounded-sm" variant="outline" data-testid="arrived-btn">
                      Arrived
                    </Button>
                    <Button onClick={() => setCompleteOpen(true)} className="w-full rounded-sm bg-[hsl(var(--muted-green-dark))] hover:bg-[hsl(var(--muted-green-dark))]/90 text-white" data-testid="mark-delivered-btn">
                      <Camera className="h-4 w-4 mr-1" /> Mark delivered
                    </Button>
                    <Button onClick={() => setFailOpen(true)} variant="outline" className="w-full rounded-sm border-destructive text-destructive hover:bg-destructive/10" data-testid="mark-failed-btn">
                      Failed delivery
                    </Button>
                  </>
                )}
                {detail.status === 'Arrived' && (
                  <>
                    <Button onClick={() => setCompleteOpen(true)} className="w-full rounded-sm bg-[hsl(var(--muted-green-dark))] hover:bg-[hsl(var(--muted-green-dark))]/90 text-white" data-testid="mark-delivered-btn">
                      <Camera className="h-4 w-4 mr-1" /> Mark delivered
                    </Button>
                    <Button onClick={() => setFailOpen(true)} variant="outline" className="w-full rounded-sm border-destructive text-destructive hover:bg-destructive/10" data-testid="mark-failed-btn">
                      Failed delivery
                    </Button>
                  </>
                )}
                {['Delivered','Failed'].includes(detail.status) && (
                  <ViewPhotoButton deliveryId={detail.id} hasPhoto={!!detail.photo_path} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <CompleteDialog
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
        deliveryId={detail?.id}
        onDone={() => { setCompleteOpen(false); setDetailId(null); reloadDeliveries(); reloadOrders(); }}
      />
      <FailDialog
        open={failOpen}
        onClose={() => setFailOpen(false)}
        deliveryId={detail?.id}
        onDone={() => { setFailOpen(false); setDetailId(null); reloadDeliveries(); reloadOrders(); }}
      />
    </div>
  );
}

function InfoBlock({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="velvet-eyebrow flex items-center gap-2 mb-2"><Icon className="h-3.5 w-3.5" /> {label}</div>
      {children}
    </div>
  );
}

function ViewPhotoButton({ deliveryId, hasPhoto }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!hasPhoto) return <p className="text-sm text-muted-foreground text-center">Delivery complete.</p>;
  const load = async () => {
    setBusy(true);
    try {
      const r = await apiFetch(`/deliveries/${deliveryId}/photo-url`);
      setUrl(r.url);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return url ? (
    <img src={url} alt="Delivery evidence" className="w-full border border-border" data-testid="delivery-photo" />
  ) : (
    <Button onClick={load} disabled={busy} variant="outline" className="w-full rounded-sm" data-testid="view-photo-btn">
      {busy ? 'Loading…' : 'View delivery photo'}
    </Button>
  );
}

function CompleteDialog({ open, onClose, deliveryId, onDone }) {
  const [recipient, setRecipient] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error('Photo required');
    if (!recipient.trim()) return toast.error('Recipient or safe-drop location required');
    setBusy(true);
    try {
      // Client-side compress
      const compressed = await compressImage(file, 1400, 0.82);
      const fd = new FormData();
      fd.append('photo', compressed, 'delivery.jpg');
      fd.append('recipient_name', recipient.trim());
      if (notes.trim()) fd.append('notes', notes.trim());
      await apiFetch(`/deliveries/${deliveryId}/complete`, { method: 'POST', body: fd });
      toast.success('Delivery marked complete');
      setRecipient(''); setNotes(''); setFile(null);
      onDone();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="complete-delivery-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Confirm delivery</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label>Photo of delivered arrangement *</Label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:border file:border-border file:bg-background file:font-medium file:text-sm"
              data-testid="delivery-photo-input"
            />
            {file && <div className="text-xs text-muted-foreground mt-1">{file.name} · {(file.size/1024/1024).toFixed(2)} MB</div>}
          </div>
          <div>
            <Label>Recipient name or safe-drop location *</Label>
            <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} className="rounded-sm" placeholder="e.g. Amelia W. (in person) or Front desk" data-testid="recipient-input" />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" />
          </div>
          <div className="text-xs text-muted-foreground">Timestamp is captured automatically in America/Chicago.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !file || !recipient.trim()} onClick={submit} className="rounded-sm bg-[hsl(var(--muted-green-dark))] hover:bg-[hsl(var(--muted-green-dark))]/90 text-white" data-testid="submit-delivery-btn">
            {busy ? 'Uploading…' : 'Confirm delivered'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FailDialog({ open, onClose, deliveryId, onDone }) {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error('Reason required');
    setBusy(true);
    try {
      await apiFetch(`/deliveries/${deliveryId}/fail`, {
        method: 'POST', body: JSON.stringify({ reason: reason.trim(), notes: notes.trim() || null }),
      });
      toast.success('Marked failed');
      setReason(''); setNotes('');
      onDone();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="fail-delivery-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Failed delivery</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label>Reason *</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-sm" placeholder="Not home, refused, wrong address…" data-testid="fail-reason-input" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !reason.trim()} onClick={submit} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="submit-fail-btn">
            {busy ? 'Saving…' : 'Save failure'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- image compression ----------
async function compressImage(file, maxSide = 1400, quality = 0.82) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = URL.createObjectURL(file);
  });
  let { width, height } = img;
  const scale = Math.min(1, maxSide / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);
  URL.revokeObjectURL(img.src);
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
  return new File([blob], 'delivery.jpg', { type: 'image/jpeg' });
}
